import { FunctionComponent, ReactElement, useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { RpcError } from "grpc-web";

// Hooks
import { useAuth } from "./hooks/useAuth";

// Utils
import { ClaimsCookies } from "./utils/types";
import { isRefreshTokenExpired, shouldRefreshToken } from "./utils/auth";

// Components
import LoadingSpinner from "./shared/LoadingSpinner/LoadingSpinner";
import useClaimsToast from "./hooks/useToast";

interface PrivateRouteProps {}

/**
 * Handles validating users are authenticated to protect private routes
 */
const PrivateRoute: FunctionComponent<PrivateRouteProps> = (props: PrivateRouteProps): ReactElement => {
	// State
	const [isLoading, setIsLoading] = useState<boolean>(false);

	// Hooks
	const { getCookies, handleJwtRefresh, removeCookies } = useAuth();
	const cookies: ClaimsCookies | undefined = getCookies();
	const toast = useClaimsToast();
	const location = useLocation();

	/**
	 * Utility function to determine whether we should redirect back to the login page
	 * Possible cases include manual routing without authn or an expired refresh token
	 * @returns True or False
	 */
	const shouldLoginAgain = useCallback((): boolean => {
		if (!cookies || !cookies.user || !cookies.refreshToken || !cookies.jwtToken) {
			return true;
		}

		// Check if refresh token has expired
		if (isRefreshTokenExpired(cookies.refreshToken.expires)) {
			return true;
		}

		return false;
	}, [cookies]);

	/**
	 * Utility function to determine whether we should refresh the JWT Token
	 * @returns True if token should be refreshed
	 */
	const shouldRefreshJwt = useCallback((): boolean => {
		if (!cookies?.jwtToken?.exp) {
			return true;
		}
		return shouldRefreshToken(cookies.jwtToken.exp);
	}, [cookies]);

	// Check authentication status once on mount
	useEffect(() => {
		const checkAuth = async () => {
			setIsLoading(true);

			if (shouldRefreshJwt()) {
				try {
					await handleJwtRefresh();
				} catch (err) {
					toast("Error", (err as RpcError).message, "error");
				}
			}
			setIsLoading(false);
		};
		checkAuth();
	}, []);

	if (isLoading) {
		return <LoadingSpinner />;
	}

	if (shouldLoginAgain()) {
		// Store the current URL to redirect back after login
		const currentPath = location.pathname + location.search;
		if (currentPath !== "/login") {
			localStorage.setItem("lastVisitedPage", currentPath);
		}
		localStorage.setItem("sessionExpired", "true");
		removeCookies();
		return <Navigate to="/login" replace />;
	}

	return <Outlet context={{ user: cookies?.user }} />;
};

export default PrivateRoute;
